// src/components/SocialEvidencePanel.jsx
// Social Evidence Panel — shown when a user taps "Community Evidence" on an incident card.
//
// Displays in strict hierarchy order:
//   Official sources first (NWS / NOAA / USGS / DOTD)
//   Then community media (photos, videos)
//   Then community posts
//   With a clear disclaimer that social evidence is supporting only
//
// Two variants:
//   <SocialEvidenceInline />  — compact chip shown on the incident card
//   <SocialEvidencePanel />   — full panel, shown in a BottomSheet on tap
//
// Data sources:
//   report.socialEvidence      — {photoCount, videoCount, postCount, label, strongEvidence}
//   report.evidenceLabels[]    — official + community source strings
//   report.mediaUrls[]         — photos/videos attached to the report itself
//
// The panel never claims this evidence is independently verified.
// It always shows the disclaimer and always surfaces official sources first.

import { useState }      from 'react';
import { BottomSheet }   from '@components/ui';
import { T }             from '@components/theme';

// ── Disclaimer text ───────────────────────────────────────────────
const DISCLAIMER =
  'Community evidence has not been independently verified. ' +
  'Use in combination with official sources. Do not act on community ' +
  'evidence alone during life-threatening situations.';

// ── Source chip for official sources ─────────────────────────────
function OfficialChip({ label }) {
  const config =
    /NWS/i.test(label)     ? { icon: '📡', color: '#059669', name: 'NWS Alert'    } :
    /NOAA|surge/i.test(label) ? { icon: '🌊', color: '#059669', name: 'NOAA Data' } :
    /USGS|gauge/i.test(label) ? { icon: '📊', color: '#059669', name: 'USGS Gauge'} :
    /DOTD|511/i.test(label)   ? { icon: '🚧', color: '#0369a1', name: 'DOTD 511'  } :
    /Entergy/i.test(label)    ? { icon: '⚡', color: '#7c3aed', name: 'Entergy'   } :
    /GOHSEP/i.test(label)     ? { icon: '🏛', color: '#0369a1', name: 'GOHSEP'   } : null;

  if (!config) return null;
  return (
    <div style={{
      display:     'flex', alignItems: 'center', gap: 8,
      padding:     '6px 10px',
      background:  `${config.color}10`,
      border:      `1px solid ${config.color}30`,
      borderRadius: 8, marginBottom: 5,
    }}>
      <span style={{ fontSize: 16 }}>{config.icon}</span>
      <div>
        <div style={{ fontSize: 12, fontWeight: 800, color: config.color }}>{config.name}</div>
        <div style={{ fontSize: 9, color: '#4b5563', lineHeight: 1.4 }}>
          {label.replace(/^(Area:|Direct:)\s*/i, '')}
        </div>
      </div>
      <span style={{ marginLeft: 'auto', fontSize: 9, fontWeight: 700,
                     color: config.color, background: `${config.color}15`,
                     padding: '2px 6px', borderRadius: 4, flexShrink: 0 }}>
        ✓ Official
      </span>
    </div>
  );
}

// ── Photo thumbnail ───────────────────────────────────────────────
function PhotoThumb({ url, index }) {
  const [loaded, setLoaded] = useState(false);
  const isVideo = /\.(mp4|mov|webm)/i.test(url) || url.includes('video');

  return (
    <div style={{
      position:     'relative',
      width:         '100%',
      aspectRatio:  '16/9',
      background:    '#111',
      borderRadius:  8,
      overflow:     'hidden',
      border:        `1px solid ${T.bdr}`,
    }}>
      {isVideo ? (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: '100%', gap: 8,
        }}>
          <span style={{ fontSize: 28 }}>▶</span>
          <span style={{ fontSize: 12, color: T.dim }}>Video {index + 1}</span>
        </div>
      ) : (
        <>
          {!loaded && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#111', color: T.dim, fontSize: 11,
            }}>
              Loading…
            </div>
          )}
          <img
            src={url}
            alt={`Community photo ${index + 1}`}
            onLoad={() => setLoaded(true)}
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
              opacity: loaded ? 1 : 0, transition: 'opacity 0.2s',
            }}
          />
        </>
      )}
      <div style={{
        position:   'absolute', bottom: 4, left: 4,
        fontSize:    8, fontWeight: 700,
        background: 'rgba(0,0,0,0.65)', color: '#d1d5db',
        padding:    '1px 5px', borderRadius: 3,
      }}>
        {isVideo ? '📹 Video' : '📸 Photo'}
      </div>
    </div>
  );
}

// ── Compact inline chip for incident card ─────────────────────────
export function SocialEvidenceInline({ report, onTap }) {
  const social = report.socialEvidence;
  if (!social || (social.photoCount + social.videoCount + social.postCount) === 0) {
    return null;
  }

  const parts = [
    social.photoCount > 0 && `${social.photoCount} photo${social.photoCount > 1 ? 's' : ''}`,
    social.videoCount > 0 && `${social.videoCount} video${social.videoCount > 1 ? 's' : ''}`,
    social.postCount  > 0 && `${social.postCount} post${social.postCount > 1 ? 's' : ''}`,
  ].filter(Boolean);

  return (
    <button
      onClick={(e) => { e.stopPropagation(); onTap?.(); }}
      aria-label={`Community evidence: ${parts.join(', ')}. Tap to view.`}
      style={{
        display:     'inline-flex', alignItems: 'center', gap: 5,
        padding:     '3px 8px', borderRadius: 6,
        background:  'rgba(217,119,6,0.09)',
        border:      '1px solid rgba(217,119,6,0.25)',
        cursor:      'pointer', fontFamily: T.font,
      }}
    >
      <span style={{ fontSize: 11 }}>
        {social.photoCount + social.videoCount > 0 ? '📸' : '💬'}
      </span>
      <span style={{ fontSize: 10, fontWeight: 700, color: T.amber }}>
        {parts.join(' · ')}
      </span>
    </button>
  );
}

// ── Full panel content ────────────────────────────────────────────
export function SocialEvidencePanel({ report, onClose }) {
  const social        = report.socialEvidence ?? {};
  const evidenceLabels = report.evidenceLabels ?? [];
  const mediaUrls     = report.mediaUrls ?? [];

  // Split evidence labels into official vs community
  const officialLabels    = evidenceLabels.filter(e =>
    /NWS|NOAA|USGS|DOTD|Entergy|GOHSEP/i.test(e)
  );
  const communityLabels   = evidenceLabels.filter(e =>
    /nearby|neighbor|confirm/i.test(e)
  );

  const hasAnyEvidence =
    officialLabels.length + communityLabels.length + mediaUrls.length > 0;

  const confPct = Math.round((report.confidenceMid ?? 0) * 100);

  return (
    <div style={{ fontFamily: T.font }}>
      {/* ── Header ─────────────────────────────────────────────── */}
      <div style={{
        padding:       '14px 18px 12px',
        borderBottom:  '1px solid rgba(255,255,255,0.07)',
        display:       'flex', alignItems: 'flex-start', gap: 12,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 900, fontSize: 16, color: T.text, marginBottom: 3 }}>
            Community Evidence
          </div>
          <div style={{ fontSize: 10, color: '#6b7280', lineHeight: 1.5 }}>
            Supporting evidence from the Eye on Storm community.
            Official sources are weighted higher in the confidence score.
          </div>
        </div>
        {confPct > 0 && (
          <div style={{
            flexShrink: 0, padding: '6px 10px',
            background: 'rgba(255,255,255,0.05)', borderRadius: 8,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: T.text }}>{confPct}%</div>
            <div style={{ fontSize: 8, color: '#6b7280' }}>confidence</div>
          </div>
        )}
      </div>

      <div style={{ overflowY: 'auto', maxHeight: '70vh', padding: '12px 18px' }}>

        {/* ── 1. Official sources (always first, always highest trust) ── */}
        {officialLabels.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{
              fontSize: 9, fontWeight: 800, color: '#4b5563',
              textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 7,
            }}>
              Official Sources
            </div>
            {officialLabels.map((label, i) => (
              <OfficialChip key={i} label={label} />
            ))}
          </div>
        )}

        {/* ── 2. Community corroboration count ──────────────────── */}
        {communityLabels.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{
              fontSize: 9, fontWeight: 800, color: '#4b5563',
              textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 7,
            }}>
              Nearby Reports
            </div>
            {communityLabels.map((label, i) => (
              <div key={i} style={{
                display:     'flex', alignItems: 'center', gap: 8,
                padding:     '6px 10px', marginBottom: 4,
                background:  'rgba(217,119,6,0.06)',
                border:      '1px solid rgba(217,119,6,0.18)',
                borderRadius: 8,
              }}>
                <span style={{ fontSize: 16 }}>👥</span>
                <span style={{ fontSize: 11, color: T.text }}>{label}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── 3. Community media ─────────────────────────────────── */}
        {mediaUrls.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{
              fontSize: 9, fontWeight: 800, color: '#4b5563',
              textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 7,
            }}>
              Community Photos & Videos
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: mediaUrls.length === 1 ? '1fr' : '1fr 1fr',
              gap: 6,
            }}>
              {mediaUrls.slice(0, 4).map((url, i) => (
                <PhotoThumb key={i} url={url} index={i} />
              ))}
            </div>
            {mediaUrls.length > 4 && (
              <div style={{ fontSize: 10, color: T.dim, marginTop: 5, textAlign: 'center' }}>
                +{mediaUrls.length - 4} more
              </div>
            )}
          </div>
        )}

        {/* ── 4. Social evidence summary from scoring engine ────── */}
        {(social.photoCount > 0 || social.videoCount > 0 || social.postCount > 0) && (
          <div style={{ marginBottom: 14 }}>
            <div style={{
              fontSize: 9, fontWeight: 800, color: '#4b5563',
              textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 7,
            }}>
              Community Evidence Summary
            </div>
            <div style={{
              display: 'flex', gap: 8, flexWrap: 'wrap',
            }}>
              {social.photoCount > 0 && (
                <div style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.04)',
                              border: `1px solid ${T.bdr}`, borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: T.text }}>{social.photoCount}</div>
                  <div style={{ fontSize: 10, color: T.dim }}>
                    Public Photo{social.photoCount !== 1 ? 's' : ''}
                  </div>
                </div>
              )}
              {social.videoCount > 0 && (
                <div style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.04)',
                              border: `1px solid ${T.bdr}`, borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: T.text }}>{social.videoCount}</div>
                  <div style={{ fontSize: 10, color: T.dim }}>
                    Public Video{social.videoCount !== 1 ? 's' : ''}
                  </div>
                </div>
              )}
              {social.postCount > 0 && (
                <div style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.04)',
                              border: `1px solid ${T.bdr}`, borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: T.text }}>{social.postCount}</div>
                  <div style={{ fontSize: 10, color: T.dim }}>
                    Public Post{social.postCount !== 1 ? 's' : ''}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── No evidence state ──────────────────────────────────── */}
        {!hasAnyEvidence && (
          <div style={{
            padding:     '20px', textAlign: 'center',
            color:        T.dim, fontSize:  11,
          }}>
            No community evidence found yet for this report.
          </div>
        )}

        {/* ── Disclaimer — always shown ───────────────────────────── */}
        <div style={{
          padding:     '10px 12px',
          background:  'rgba(220,38,38,0.06)',
          border:      '1px solid rgba(220,38,38,0.18)',
          borderRadius: 8, marginBottom: 8,
          fontSize:     10, color: '#9ca3af', lineHeight: 1.6,
        }}>
          <span style={{ fontSize: 12, marginRight: 4 }}>⚠</span>
          {DISCLAIMER}
        </div>

      </div>

      {/* Close button */}
      <div style={{ padding: '4px 18px 18px' }}>
        <button
          onClick={onClose}
          style={{
            width: '100%', padding: '11px', borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'transparent', color: '#6b7280',
            fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: T.font,
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ── SocialEvidenceButton — tap trigger on incident card ───────────
export function SocialEvidenceButton({ report }) {
  const [open, setOpen] = useState(false);
  const social = report.socialEvidence;

  if (!social || social.totalItems === 0) return null;

  return (
    <>
      <SocialEvidenceInline report={report} onTap={() => setOpen(true)} />
      <BottomSheet open={open} onClose={() => setOpen(false)} maxH="90vh">
        <SocialEvidencePanel report={report} onClose={() => setOpen(false)} />
      </BottomSheet>
    </>
  );
}
